import {
  Avatar,
  Box,
  Divider,
  Paper,
  Typography,
  TypographyOwnProps,
} from "@mui/material";
import { FC, PropsWithChildren } from "react";
import { useAppDataContext } from "@context/AppContext";
import ClockAndStartTime from "./header/ClockAndStartTime";
import { useGameDataContext } from "@context/GameDataContext";
import HeaderBackButton from "./header/HeaderBackButton";
import HeaderConnectionIndicator from "./header/HeaderConnectionIndicator";
import DownloadPdfButton from "../DownloadPdfButton";
import OpenScoreSheetButton from "../OpenScoreSheetButton";
import AppVersion from "../AppVersion";
import CurrentScore from "../CurrentScore";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { GameProgress } from "@/models/gameState";
import { gameState, useTeamSides } from "@/services/gameState";
import OpenSettingsButton from "../OpenSettingsButton";
import RemainingRow from "../table/RemainingRow";
import TableRowDivider from "../table/TableRowDivider";

const ChallengeDetailPageHeader: FC = () => {
  const { onTablet } = useResponsiveContext();
  const { game } = useGameDataContext();
  const gameProgress = gameState("gameProgress");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const TeamName: FC<
    PropsWithChildren<{ align: TypographyOwnProps["align"] }>
  > = ({ children, align }) => {
    return (
      <Typography
        align={align}
        variant={"h5"}
        flex={1}
        overflow={"hidden"}
        whiteSpace={"nowrap"}
        textOverflow={"ellipsis"}
      >
        {children}
      </Typography>
    );
  };

  return (
    <Paper
      elevation={5}
      sx={{ px: onTablet ? 1 : 5, py: 1, borderRadius: 0, flexShrink: 0 }}
    >
      <Box
        alignItems="center"
        display={"grid"}
        gridTemplateColumns={
          onTablet
            ? "auto auto"
            : "minmax(0, 1fr) auto min(65%, 840px) auto minmax(0, 1fr)"
        }
      >
        <Box display={"flex"} alignItems={"center"} gap={2}>
          <HeaderBackButton navigateTo={"/"} tooltip={"Zur Spielübersicht"} />
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            flexWrap={"wrap"}
            alignItems={"center"}
          >
            <HeaderConnectionIndicator />
            {!onTablet && (
              <>
                <DownloadPdfButton
                  game={game}
                  highlight={
                    gameProgress.valueOf() >= GameProgress.GAME_FINISHED
                  }
                />
                <OpenScoreSheetButton />
                <OpenSettingsButton />
              </>
            )}
          </Box>
        </Box>
        {!onTablet && (
          <>
            <Divider orientation={"vertical"} />
            <Box px={"1vw"}>
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                gap={2}
              >
                {leftTeam && rightTeam && (
                  <>
                    <TeamName align={"right"}>
                      {game?.gameData[leftTeam].longName}
                    </TeamName>
                    {game?.gameData[leftTeam].iconUrl && (
                      <Avatar
                        src={game?.gameData[leftTeam].iconUrl}
                        sx={{ flex: "0 0 auto" }}
                      />
                    )}
                    <Box
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      flexDirection={"column"}
                      flex={"0 0 auto"}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <CurrentScore
                          sx={{ flex: "0 0 auto" }}
                          hideOnLost={true}
                          fontSize={"28px"}
                          withSetScore={true}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          justifySelf: "center",
                          width: "100%",
                        }}
                      >
                        <TableRowDivider />
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto 1fr",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            fontSize: "1.2em",
                          }}
                        >
                          <RemainingRow type={"challenges"} />
                        </Box>
                        <TableRowDivider />
                      </Box>
                    </Box>

                    {game?.gameData[rightTeam].iconUrl && (
                      <Avatar
                        src={game?.gameData[rightTeam].iconUrl}
                        sx={{ flex: "0 0 auto" }}
                      />
                    )}
                    <TeamName align={"left"}>
                      {game?.gameData[rightTeam].longName}
                    </TeamName>
                  </>
                )}
              </Box>
            </Box>
            <Divider orientation={"vertical"} />
          </>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
          }}
        >
          <ClockAndStartTime />
          {!onTablet && (
            <AppVersion align={"right"} showAlwaysOnState={false} lines={2} />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ChallengeDetailPageHeader;
